import { Component, computed, inject, signal } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { OidcService } from "../oidc.service";
import { LoggerService } from "../../logger/logger.service";
import { toError } from "../../utils/to-error";

/**
 * Persistent bar for session problems that need a decision from the user.
 *
 * Deliberately a bar rather than a modal: a background tab must never steal
 * focus with a popup, and a tab whose session ended should stay readable
 * instead of being frozen behind a backdrop the user cannot dismiss.
 *
 * Two cases, re-authentication taking precedence because without a session the
 * masquerade question is moot:
 *  - `reauth` - this tab's session ended (expired, or signed out in another tab)
 *  - `stale`  - another tab switched masquerade, so this tab is showing a
 *               different user's data than the rest of the browser
 */
@Component({
    selector: "risr-session-banner",
    imports: [MatIconModule],
    template: `
        @if (mode(); as current) {
            <div
                class="session-banner"
                [class.session-banner--reauth]="current === 'reauth'"
                [class.session-banner--stale]="current === 'stale'"
                role="status"
                aria-live="polite"
            >
                <mat-icon class="session-banner__icon" aria-hidden="true">
                    {{ current === 'reauth' ? 'lock' : 'sync_problem' }}
                </mat-icon>

                <p class="session-banner__message">
                    @if (current === 'reauth') {
                        <ng-container i18n="Session|Session ended">
                            Your session has ended. Sign in again to continue.
                        </ng-container>
                    } @else if (staleLabel(); as who) {
                        <ng-container i18n="Session|Masquerade changed elsewhere">
                            You are now signed in as {{ who }} in another tab.
                            Reload to continue as them here.
                        </ng-container>
                    } @else {
                        <ng-container i18n="Session|Masquerade ended elsewhere">
                            You stopped viewing as another user in another tab.
                            Reload to return to your own account here.
                        </ng-container>
                    }
                </p>

                @if (current === 'reauth') {
                    <button
                        type="button"
                        class="session-banner__action"
                        [disabled]="busy()"
                        (click)="signIn()"
                        i18n="Session|Sign in action"
                    >
                        Sign in
                    </button>
                } @else {
                    <button
                        type="button"
                        class="session-banner__action"
                        [disabled]="busy()"
                        (click)="adopt()"
                        i18n="Session|Reload action"
                    >
                        Reload
                    </button>
                }
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .session-banner {
                /* Uses the app's existing warning/danger palette so the bar
                   matches every other alert in the product. Fallbacks match the
                   token values because consumers that are not kaizen (assembly,
                   booking_frontend) may not define them. Measured contrast for
                   the tint/text pairs is 13.1:1 (warning) and 13.4:1 (danger). */
                --session-banner-bg: var(--color-warning-100, #fbf0ea);
                --session-banner-fg: var(--color-warning-900, #40200c);
                --session-banner-accent: var(--color-warning, #ab5620);
                --session-banner-button-bg: var(--color-warning-700, #964c1c);

                align-items: center;
                background-color: var(--session-banner-bg);
                border-left: 4px solid var(--session-banner-accent);
                color: var(--session-banner-fg);
                display: flex;
                flex-wrap: wrap;
                gap: var(--space-x, 0.64em);
                padding: var(--base-padding, 0.64em) calc(var(--base-padding, 0.64em) * 2);
            }

            .session-banner--reauth {
                --session-banner-bg: var(--color-danger-100, #ffe5e5);
                --session-banner-fg: var(--color-danger-900, #4d0000);
                --session-banner-accent: var(--color-danger, #d00000);
                --session-banner-button-bg: var(--color-danger-700, #b30000);
            }

            .session-banner__icon {
                color: var(--session-banner-accent);
                flex: none;
            }

            .session-banner__message {
                flex: 1 1 auto;
                margin: 0;
            }

            .session-banner__action {
                /* White on the -700 shade is 6.3:1 (warning) and 7.2:1 (danger). */
                background-color: var(--session-banner-button-bg);
                border: 1px solid transparent;
                border-radius: var(--border-radius, 4px);
                color: #ffffff;
                cursor: pointer;
                flex: none;
                font: inherit;
                font-weight: 600;
                padding: calc(var(--space-x, 0.64em) / 2) var(--space-x, 0.64em);
            }

            .session-banner__action:hover:not(:disabled) {
                opacity: 0.9;
            }

            .session-banner__action:disabled {
                cursor: default;
                opacity: 0.7;
            }

            .session-banner__action:focus-visible {
                outline: 2px solid var(--session-banner-fg);
                outline-offset: 2px;
            }
        `
    ],
})
export class SessionBannerComponent {

    private oidc = inject(OidcService);
    private logger = inject(LoggerService).scope('SessionBanner');

    protected readonly busy = signal(false);

    /**
     * Prefer the name the switching tab recorded; fall back to the identifier
     * when it is unknown, which is better than saying nothing at all.
     */
    protected readonly staleLabel = computed(() => {
        const stale = this.oidc.$staleSession();
        if (!stale?.masquerade) {
            return null;
        }
        return stale.name ? `${stale.name} (${stale.masquerade})` : stale.masquerade;
    });

    protected readonly mode = computed<'reauth' | 'stale' | null>(() => {
        if (this.oidc.$needsReauth()) {
            return 'reauth';
        }
        if (this.oidc.$staleSession()) {
            return 'stale';
        }
        return null;
    });

    protected async signIn(): Promise<void> {
        if (this.busy()) {
            return;
        }
        this.busy.set(true);
        try {
            await this.oidc.reauthenticate();
        } catch (err: unknown) {
            this.logger.warn('Re-authentication from banner failed', undefined, toError(err));
        } finally {
            this.busy.set(false);
        }
    }

    protected async adopt(): Promise<void> {
        if (this.busy()) {
            return;
        }
        this.busy.set(true);
        try {
            // Navigates away on success, so `busy` is only cleared on failure.
            await this.oidc.adoptSharedMasquerade();
        } catch (err: unknown) {
            this.logger.warn('Could not follow masquerade change', undefined, toError(err));
            this.busy.set(false);
        }
    }
}
